import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import './MemoryDAG.css';
import ZepMemory from './ZepMemory';

// Import components
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import ChatMessages from './components/ChatMessages';
import ChatInput from './components/ChatInput';
import Login from './components/Login';

// Import hooks
import { useTheme } from './hooks/useTheme';
import { useCustomPrompt } from './hooks/useCustomPrompt';
import { useMemory } from './hooks/useMemory';

// Import utilities
import { extractTextFromFile, chunkLargeText } from './utils/fileProcessing';
import { performWebSearch, searchRAG } from './utils/searchUtils';
import { buildSystemPrompt, buildRAGSystemPrompt } from './utils/promptUtils';
import { performStructuredRouting, formatRoutingDecision } from './utils/agentRouting';
import { handleDocumentRewriterWithLangGraph, resetDocumentRewriterAgent } from './utils/documentRewriterLangGraph';
import { createOpenAIProxy } from './utils/openaiProxy';



const App = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-4.1');
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [ragEnabled, setRagEnabled] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('chat');

  // Agent Mode States
  const [agentMode, setAgentMode] = useState(true);
  const [agentStatus, setAgentStatus] = useState('');
  const [documentRewriterMode, setDocumentRewriterMode] = useState(false);

  // Use custom hooks
  const { isDarkTheme, toggleTheme } = useTheme();
  const {
    customPrompt,
    promptInputValue,
    setPromptInputValue,
    showPromptEditor,
    setShowPromptEditor,
    updateCustomPrompt,
    resetToDefaultPrompt
  } = useCustomPrompt();
  const {
    memoryEnabled,
    setMemoryEnabled,
    setConversationId,
    memoryStats,
    relevantMemories,
    setRelevantMemories,
    showMemoryPanel,
    setShowMemoryPanel,
    sessionId,
    fetchMemoryStats,
    generateConversationId
  } = useMemory();

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  // When true we ignore streaming tokens (used when user clicks New chat)
  const abortStreamRef = useRef(false);

  // Note: OpenAI API calls now routed through secure backend proxy
  // No API key needed in frontend for security

  // Login handler
  const handleLogin = () => {
    setIsAuthenticated(true);
  };









  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Track message count to only scroll on NEW messages, not content updates or source toggles
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    // Only scroll if a NEW message was added (not just content updated or sources toggled)
    if (messages.length > prevMessageCountRef.current) {
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);



  // LangGraph Supervisor Agent (Backend Implementation)

  const clearChat = () => {
    setMessages([]);
    setAttachedFiles([]);
    setRelevantMemories([]);
    setShowMemoryPanel(false);
    setAgentStatus('');
    setDocumentRewriterMode(false);
    setConversationId(generateConversationId());
    abortStreamRef.current = true;
    setTimeout(() => abortStreamRef.current = false, 100);
  };

  // New memory-enabled message sending function
  const sendMessageWithMemory = async () => {
    if (!input.trim() || isLoading) return;

    // Detect if the current message is a correction (e.g. starts with "correction" or contains "correction to add")
    const isCorrection = input.trim().toLowerCase().startsWith('correction') ||
      input.trim().toLowerCase().includes('correction to add');

    // If it's a correction, attempt to store it in the DAG before proceeding
    if (isCorrection && memoryEnabled) {
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant' && m.memoryNodeId);
      if (lastAssistant && lastAssistant.memoryNodeId) {
        try {
          await fetch('/api/memory/correction', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              node_id: lastAssistant.memoryNodeId,
              correction_text: input.trim()
            })
          });
          console.log('🧠 Added correction to DAG');
        } catch (corrErr) {
          console.error('❌ Failed to add correction:', corrErr);
        }
      }
    }

    const userMessage = { role: 'user', content: input.trim() };
    const history = [...messages, userMessage];
    setMessages(history);
    const originalInput = input.trim();
    setInput('');
    setIsLoading(true);

    try {



      // Use the exact same working logic as sendMessage function
      const openai = createOpenAIProxy();

      // ------ Handle file attachments (same as sendMessage) ------
      let extractedFileTexts = [];
      let fileProcessingErrors = [];

      if (attachedFiles.length > 0) {
        console.log(`📁 Processing ${attachedFiles.length} files locally...`);

        // Process files locally (extract text)
        for (const file of attachedFiles) {
          try {
            console.log(`📤 Processing: ${file.name}`);
            const extractedText = await extractTextFromFile(file);
            if (extractedText) {
              extractedFileTexts.push({
                name: file.name,
                content: extractedText // No limit - include full text
              });
              console.log(`✅ Processed: ${file.name} (${extractedText.length} characters)`);
            } else {
              fileProcessingErrors.push(`Failed to extract text from ${file.name}`);
            }
          } catch (processingError) {
            console.error(`❌ Failed to process ${file.name}:`, processingError);
            fileProcessingErrors.push(`Failed to process ${file.name}: ${processingError.message}`);
          }
        }

        // If file processing failed, show error and return
        if (fileProcessingErrors.length > 0 && extractedFileTexts.length === 0) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ Failed to process files:\n${fileProcessingErrors.join('\n')}\n\nPlease try again with supported file types (PDF, text, Word documents).`
          }]);
          setIsLoading(false);
          setAttachedFiles([]);
          return;
        }

        // Update user message to show which files were successfully processed
        if (extractedFileTexts.length > 0) {
          const successfulFiles = extractedFileTexts.map(f => f.name).join(', ');
          history[history.length - 1].content += `\n\n[Files attached: ${successfulFiles}]`;
          setMessages([...history]);
        }
      }

      // STEP 1 - Declare variables first to avoid initialization errors
      let agentToolsUsed = [];
      let enhancedQuery = originalInput;  // Will be enhanced by memory if available
      let memoryContext = "";

      // STEP 2 - Always search memory first (up to 5 results) 
      if (memoryEnabled) {
        try {
          const memoryResponse = await fetch('/api/zep/search', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              query: originalInput,
              limit: 5  // Always search up to 5 memory results
            })
          });

          if (memoryResponse.ok) {
            const memoryData = await memoryResponse.json();
            if (memoryData.success) {
              if (memoryData.results && memoryData.results.length > 0) {
                agentToolsUsed.push('memory_search');

                // Use enhanced query for better RAG/web searches
                enhancedQuery = memoryData.enhanced_query || originalInput;

                // Use formatted memory context
                memoryContext = memoryData.formatted_context || "";

                setRelevantMemories(memoryData.results.map(result => ({
                  user_message: "Zep memory",
                  assistant_response: typeof result === 'string' ? result : result.fact,
                  similarity: typeof result === 'string' ? 0.8 : (result.score || 0.8),
                  timestamp: new Date().toISOString(),
                  corrections: []
                })));
                console.log(`🧠 Memory search found ${memoryData.results.length} results - enhanced query: "${enhancedQuery}"`);
              } else {
                // Always set memory context so AI knows memory was searched
                memoryContext = "🧠 MEMORY SEARCH: No stored information found for this query. Memory system is active and available for storing new information.";
                setRelevantMemories([]);
              }
            }
          }
        } catch (memoryError) {
          console.warn('Memory search tool failed:', memoryError);
        }
      }

      // STEP 3 - Use enhanced query for better searches
      const contextBlocks = [];
      let webCitations = [];
      let ragCitations = [];

      // Add memory context first (highest priority)
      if (memoryContext) {
        contextBlocks.push(memoryContext);
      }

      const searchPromises = [];
      if (webSearchEnabled) {
        // Use enhanced query for better web search
        searchPromises.push(
          performWebSearch(enhancedQuery, openai, history, selectedModel).then(r => ({ tag: 'Web search', res: r }))
        );
      }
      if (ragEnabled) {
        // Use enhanced query for better RAG search
        searchPromises.push(
          searchRAG(enhancedQuery, openai, history, selectedModel, () => buildRAGSystemPrompt(customPrompt)).then(r => ({ tag: 'Knowledge-base context', res: r }))
        );
      }

      if (searchPromises.length) {
        const results = await Promise.all(searchPromises);
        results.forEach(({ tag, res }) => {
          if (res && res.success && res.content) {
            contextBlocks.push(`${tag}:\n${res.content}`);
            if (tag === 'Web search') webCitations = res.citations || [];
            if (tag === 'Knowledge-base context') ragCitations = res.citations || [];
          }
        });
      }

      // Add file content to context if files were processed
      if (extractedFileTexts.length > 0) {
        const fileContext = extractedFileTexts.map(file => {
          const chunkedContent = chunkLargeText(file.content);
          return `[UPLOADED FILE: ${file.name}]\n${chunkedContent}`;
        }).join('\n\n');
        contextBlocks.push(`Uploaded files content:\n${fileContext}`);
      }



      // Build messages for OpenAI - standard format (same as sendMessage)
      const openaiMessages = [
        { role: 'system', content: contextBlocks.length ? `${buildSystemPrompt(customPrompt)}\n\n${contextBlocks.join('\n\n')}` : buildSystemPrompt(customPrompt) },
        ...history,
      ];

      // Generate agent tools message prefix
      let toolsMessage = "";
      if (agentToolsUsed.length > 0) {
        toolsMessage = `🤖 Agent used: ${agentToolsUsed.join(' + ')}\n\n`;
      }

      // Add knowledge base to tools used (before streaming starts)
      if (ragEnabled && ragCitations.length > 0) {
        agentToolsUsed.push('knowledge_base');
        toolsMessage = `🤖 Agent used: ${agentToolsUsed.join(' + ')}\n\n`;
      }

      // Start streaming response (same as sendMessage)
      const stream = await openai.chat.completions.create({
        model: selectedModel,
        messages: openaiMessages,
        temperature: 1.0,
        top_p: 1.0,
        presence_penalty: 0,
        frequency_penalty: 0,
        stream: true
      });

      // Add empty assistant message that will be updated with streaming content
      let assistantContent = '';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: toolsMessage,
        webCitations,
        ragCitations,
        showSources: false
      }]);

      // Stream response token by token
      for await (const chunk of stream) {
        if (abortStreamRef.current) break;
        const delta = chunk.choices?.[0]?.delta?.content || '';
        if (delta) {
          assistantContent += delta;
          const currentContent = toolsMessage + assistantContent;
          setMessages(prev => {
            const upd = [...prev];
            upd[upd.length - 1] = { ...upd[upd.length - 1], content: currentContent };
            return upd;
          });
        }
      }

      // 🤖 AGENT TOOLS: Enhanced Memory saving with GPT layer (after streaming completes)
      if (memoryEnabled && input.trim() && assistantContent) {
        try {
          // Use enhanced endpoint with GPT layer for better memory creation
          const memoryResponse = await fetch('/api/zep/add_conversation_enhanced', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_message: originalInput,
              session_id: sessionId,
              assistant_response: assistantContent,
              use_gpt_layer: true
            })
          });

          if (memoryResponse.ok) {
            const result = await memoryResponse.json();
            if (result.success) {
              if (result.saved) {
                if (result.gpt_layer_used) {
                  console.log(`🧠 Agent tool: memory_save_gpt - Enhanced memory: ${result.enhanced_memory?.substring(0, 100)}...`);
                } else {
                  console.log(`🧠 Agent tool: memory_save - ${result.reason}`);
                }
              } else {
                console.log(`🤖 Agent decided NOT to save: ${result.reason}`);
              }
            }
          }
        } catch (memoryError) {
          console.warn('Enhanced memory save tool failed:', memoryError);
        }
      }

      setAttachedFiles([]);

      // Update memory stats after storing interaction
      if (memoryEnabled) {
        fetchMemoryStats();
      }

    } catch (error) {
      console.error('❌ Send message error:', error);
      let errorMessage = 'Sorry, an error occurred. Please try again.';

      if (error.message?.includes('file')) {
        errorMessage = '❌ File processing failed. Please check your files and try again.';
      } else if (error.message?.includes('context_length_exceeded')) {
        errorMessage = '❌ Document processed but content is extremely large. The system has automatically chunked the document and analyzed the first section. For full document analysis, try breaking it into smaller files.';
      } else if (error.message?.includes('API key')) {
        errorMessage = '❌ API key issue. Please check your OpenAI API key configuration.';
      }

      setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
    }

    setIsLoading(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    abortStreamRef.current = false; // reset abort flag for this run

    const userMessage = { role: 'user', content: input.trim() };
    const history = [...messages, userMessage];
    setMessages(history);
    setInput('');
    setIsLoading(true);

    try {
      const openai = createOpenAIProxy();

      // ------ Handle file attachments ------
      let extractedFileTexts = [];
      let fileProcessingErrors = [];

      if (attachedFiles.length > 0) {
        console.log(`📁 Processing ${attachedFiles.length} files locally...`);

        // Process files locally (extract text)
        for (const file of attachedFiles) {
          try {
            console.log(`📤 Processing: ${file.name}`);
            const extractedText = await extractTextFromFile(file);
            if (extractedText) {
              extractedFileTexts.push({
                name: file.name,
                content: extractedText // No limit - include full text
              });
              console.log(`✅ Processed: ${file.name} (${extractedText.length} characters)`);
            } else {
              fileProcessingErrors.push(`Failed to extract text from ${file.name}`);
            }
          } catch (processingError) {
            console.error(`❌ Failed to process ${file.name}:`, processingError);
            fileProcessingErrors.push(`Failed to process ${file.name}: ${processingError.message}`);
          }
        }

        // If file processing failed, show error and return
        if (fileProcessingErrors.length > 0 && extractedFileTexts.length === 0) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ Failed to process files:\n${fileProcessingErrors.join('\n')}\n\nPlease try again with supported file types (PDF, text, Word documents).`
          }]);
          setIsLoading(false);
          return;
        }

        // Update user message to show which files were successfully processed
        if (extractedFileTexts.length > 0) {
          const successfulFiles = extractedFileTexts.map(f => f.name).join(', ');
          history[history.length - 1].content += `\n\n[Files attached: ${successfulFiles}]`;
          setMessages([...history]);
        }
      }

      // Collect optional context blocks
      const contextBlocks = [];
      let webCitations = [];
      let ragCitations = [];

      // STEP 1 - Search memory first (up to 5 results) to enhance other searches
      let enhancedQuery = input.trim();  // Will be enhanced by memory if available
      let memoryContext = "";

      if (memoryEnabled) {
        try {
          const memoryResponse = await fetch('/api/zep/search', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              query: input.trim(),
              limit: 5  // Always search up to 5 memory results
            })
          });

          if (memoryResponse.ok) {
            const memoryData = await memoryResponse.json();
            if (memoryData.success) {
              if (memoryData.results && memoryData.results.length > 0) {
                // Use enhanced query for better RAG/web searches
                enhancedQuery = memoryData.enhanced_query || input.trim();

                // Use formatted memory context
                memoryContext = memoryData.formatted_context || "";

                // Set relevant memories for display
                setRelevantMemories(memoryData.results.map(result => ({
                  user_message: "Zep memory",
                  assistant_response: typeof result === 'string' ? result : result.fact,
                  similarity: typeof result === 'string' ? 0.8 : (result.score || 0.8),
                  timestamp: new Date().toISOString(),
                  corrections: []
                })));

                console.log(`🧠 [Regular Mode] Memory search found ${memoryData.results.length} results - enhanced query: "${enhancedQuery}"`);
              } else {
                // Always set memory context so AI knows memory was searched
                memoryContext = "🧠 MEMORY SEARCH: No stored information found for this query. Memory system is active and available for storing new information.";
                setRelevantMemories([]);
              }
            }
          }
        } catch (memoryError) {
          console.warn('[Regular Mode] Memory search failed:', memoryError);
        }
      }

      // Add memory context first (highest priority)
      if (memoryContext) {
        contextBlocks.push(memoryContext);
      }

      const searchPromises = [];
      if (webSearchEnabled) {
        // Use enhanced query for better web search
        searchPromises.push(
          performWebSearch(enhancedQuery, openai, history, selectedModel).then(r => ({ tag: 'Web search', res: r }))
        );
      }
      if (ragEnabled) {
        // Use enhanced query for better RAG search  
        searchPromises.push(
          searchRAG(enhancedQuery, openai, history, selectedModel, () => buildRAGSystemPrompt(customPrompt)).then(r => ({ tag: 'Knowledge-base context', res: r }))
        );
      }

      if (searchPromises.length) {
        const results = await Promise.all(searchPromises);
        results.forEach(({ tag, res }) => {
          if (res && res.success && res.content) {
            contextBlocks.push(`${tag} (enhanced by memory):\n${res.content}`);
            if (tag === 'Web search') webCitations = res.citations || [];
            if (tag === 'Knowledge-base context') ragCitations = res.citations || [];
          }
        });
      }

      // Add file content to context if files were processed
      if (extractedFileTexts.length > 0) {
        const fileContext = extractedFileTexts.map(file => {
          const chunkedContent = chunkLargeText(file.content);
          return `[UPLOADED FILE: ${file.name}]\n${chunkedContent}`;
        }).join('\n\n');
        contextBlocks.push(`Uploaded files content:\n${fileContext}`);
      }



      // Build messages for OpenAI - standard format
      const openaiMessages = [
        { role: 'system', content: contextBlocks.length ? `${buildSystemPrompt(customPrompt)}\n\n${contextBlocks.join('\n\n')}` : buildSystemPrompt(customPrompt) },
        ...history,
      ];

      // Start streaming response
      const stream = await openai.chat.completions.create({
        model: selectedModel,
        messages: openaiMessages,
        temperature: 1.0,
        top_p: 1.0,
        presence_penalty: 0,
        frequency_penalty: 0,
        stream: true
      });

      let assistantContent = '';
      setMessages(prev => [...prev, { role: 'assistant', content: '', webCitations, ragCitations, showSources: false }]);

      for await (const chunk of stream) {
        if (abortStreamRef.current) break;
        const delta = chunk.choices?.[0]?.delta?.content || '';
        if (delta) {
          assistantContent += delta;
          const currentContent = assistantContent;
          setMessages(prev => {
            const upd = [...prev];
            const last = upd[upd.length - 1];
            upd[upd.length - 1] = { ...last, content: currentContent };
            return upd;
          });
        }
      }

      // 🤖 AGENT TOOLS: Regular mode enhanced memory saving with GPT layer
      if (memoryEnabled && input.trim() && assistantContent) {
        try {
          // Use enhanced endpoint with GPT layer for better memory creation
          const memoryResponse = await fetch('/api/zep/add_conversation_enhanced', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_message: history[history.length - 1].content,
              session_id: sessionId,
              assistant_response: assistantContent,
              use_gpt_layer: true
            })
          });

          if (memoryResponse.ok) {
            const result = await memoryResponse.json();
            if (result.success) {
              if (result.saved) {
                if (result.gpt_layer_used) {
                  console.log(`🧠 [Regular Mode] Enhanced memory saved: ${result.enhanced_memory?.substring(0, 100)}...`);
                } else {
                  console.log(`🧠 [Regular Mode] Memory saved: ${result.reason}`);
                }
              } else {
                console.log(`🤖 [Regular Mode] NOT saved: ${result.reason}`);
              }
            }
          }
        } catch (memoryError) {
          console.warn('[Regular Mode] Enhanced memory save failed:', memoryError);
        }
      }

      setAttachedFiles([]); // clear attachments after sending

    } catch (err) {
      console.error('❌ Send message error:', err);
      let errorMessage = 'Sorry, an error occurred. Please try again.';

      // Provide more specific error messages
      if (err.message?.includes('file')) {
        errorMessage = '❌ File processing failed. Please check your files and try again.';
      } else if (err.message?.includes('context_length_exceeded')) {
        errorMessage = '❌ Document processed but content is extremely large. The system has automatically chunked the document and analyzed the first section. For full document analysis, try breaking it into smaller files.';
      } else if (err.message?.includes('API key')) {
        errorMessage = '❌ API key issue. Please check your OpenAI API key configuration.';
      }

      setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      // Use same logic as button click
      if (agentMode) {
        sendMessageWithAgent();
      } else if (memoryEnabled) {
        sendMessageWithMemory();
      } else {
        sendMessage();
      }
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAttachedFiles(prev => [...prev, ...files]);
    // Clear the input value so the same file can be selected again
    e.target.value = '';
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Document Rewriter Agent Handler using LangGraph ONLY
  const handleDocumentRewriterAgent = async (query, openai, history) => {
    try {
      setAgentStatus('🤖 LangGraph Document Rewriter Agent processing...');

      // Use ONLY the LangGraph agent - no fallbacks
      const result = await handleDocumentRewriterWithLangGraph(query, history);

      if (result.success) {
        // Check if we should exit document rewriter mode
        if (result.intent === 'exit') {
          setDocumentRewriterMode(false);
          resetDocumentRewriterAgent(); // Reset agent state when exiting
        }

        // Create the response message
        const responseMessage = {
          role: 'assistant',
          content: result.message,
          agentMode: result.intent === 'exit' ? null : 'document_rewriter',
          showSources: false,
          documentRewriterData: result.intent === 'exit' ? null : {
            selectedDocument: result.selected_document,
            entityMapping: result.entity_mapping || {},
            downloadUrl: result.downloadUrl,
            filename: result.filename
          }
        };

        return {
          success: true,
          message: responseMessage
        };
      } else {
        // Show the exact error from LangGraph
        throw new Error(`LangGraph Agent Error: ${result.message}`);
      }

    } catch (error) {
      console.error('❌ LangGraph Document Rewriter Agent error:', error);
      setAgentStatus('');

      // Show detailed error information for debugging
      const errorMessage = `❌ **LangGraph Document Rewriter Agent Error**

**Error Details:**
${error.message}

**Error Type:** ${error.name || 'Unknown'}
**Error Stack:** ${error.stack ? error.stack.split('\n').slice(0, 3).join('\n') : 'No stack trace'}

**What happened:**
The LangGraph agent encountered an error while processing your request. This could be due to:
- Network connectivity issues
- API key problems
- Backend service issues
- Invalid request format

**Next steps:**
1. Check your internet connection
2. Verify your OpenAI API key is valid
3. Try again with a simpler request
4. Say "exit" to leave document rewriter mode

**For debugging:** Check the browser console for more detailed error information.`;

      return {
        success: false,
        message: {
          role: 'assistant',
          content: errorMessage,
          agentMode: 'document_rewriter',
          showSources: false
        }
      };
    }
  };



  // NEW STRUCTURED AGENT MODE - Using OpenAI Structured Responses
  const sendMessageWithAgent = async () => {
    if (!input.trim() || isLoading) return;

    // Detect and store corrections automatically
    const isCorrection = input.trim().toLowerCase().startsWith('correction') ||
      input.trim().toLowerCase().includes('correction to add');
    if (isCorrection && memoryEnabled) {
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant' && m.memoryNodeId);
      if (lastAssistant && lastAssistant.memoryNodeId) {
        try {
          await fetch('/api/memory/correction', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              node_id: lastAssistant.memoryNodeId,
              correction_text: input.trim()
            })
          });
          console.log('🧠 [Agent Mode] Added correction to DAG');
        } catch (corrErr) {
          console.error('❌ [Agent Mode] Failed to add correction:', corrErr);
        }
      }
    }

    const userMessage = { role: 'user', content: input.trim() };
    const history = [...messages, userMessage];
    setMessages(history);
    const query = input.trim();
    setInput('');
    setIsLoading(true);
    setAgentStatus('🤖 Agent analyzing request...');

    try {
      const openai = createOpenAIProxy();

      // Handle file attachments
      let extractedFileTexts = [];
      let fileProcessingErrors = [];

      if (attachedFiles.length > 0) {
        console.log(`📁 Processing ${attachedFiles.length} files locally...`);
        setAgentStatus('🤖 Processing uploaded files...');

        for (const file of attachedFiles) {
          try {
            console.log(`📤 Processing: ${file.name}`);
            const extractedText = await extractTextFromFile(file);
            if (extractedText) {
              extractedFileTexts.push({
                name: file.name,
                content: extractedText
              });
              console.log(`✅ Processed: ${file.name} (${extractedText.length} characters)`);
            } else {
              fileProcessingErrors.push(`Failed to extract text from ${file.name}`);
            }
          } catch (processingError) {
            console.error(`❌ Failed to process ${file.name}:`, processingError);
            fileProcessingErrors.push(`Failed to process ${file.name}: ${processingError.message}`);
          }
        }

        if (fileProcessingErrors.length > 0 && extractedFileTexts.length === 0) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ Failed to process files:\n${fileProcessingErrors.join('\n')}\n\nPlease try again with supported file types (PDF, text, Word documents).`
          }]);
          setIsLoading(false);
          setAttachedFiles([]);
          setAgentStatus('');
          return;
        }

        if (extractedFileTexts.length > 0) {
          const successfulFiles = extractedFileTexts.map(f => f.name).join(', ');
          history[history.length - 1].content += `\n\n[Files attached: ${successfulFiles}]`;
          setMessages([...history]);
        }
      }

      // Check if we're in document rewriter mode - if so, ALWAYS route to document rewriter
      if (documentRewriterMode) {
        setDocumentRewriterMode(true);
        const rewriterResult = await handleDocumentRewriterAgent(query, openai, history);

        if (rewriterResult.success) {
          // Check if the result indicates we should exit document rewriter mode
          if (rewriterResult.message.agentMode === null) {
            setDocumentRewriterMode(false);
          }

          setMessages(prev => [...prev, rewriterResult.message]);
          setAgentStatus('');
          setIsLoading(false);
          setAttachedFiles([]);
          return;
        } else {
          // If document rewriter fails, show error but stay in document rewriter mode
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ **LangGraph Document Rewriter Agent Error**

**Error:** ${rewriterResult.message.content}

**Status:** Staying in document rewriter mode.

**Next steps:**
- Try again with a different request
- Say "exit" to leave document rewriter mode
- Check browser console for detailed error information`,
            agentMode: 'document_rewriter',
            showSources: false
          }]);
          setAgentStatus('');
          setIsLoading(false);
          setAttachedFiles([]);
          return;
        }
      }

      // STEP 1: Perform structured routing
      setAgentStatus('🤖 Determining best approach...');

      const routingResult = await performStructuredRouting(
        query,
        openai,
        selectedModel,
        history,
        attachedFiles
      );

      if (!routingResult.success) {
        throw new Error('Routing failed: ' + routingResult.error);
      }

      const { routing } = routingResult;
      const routingDisplay = formatRoutingDecision(routing);

      console.log(`🎯 Structured routing: ${routingDisplay.displayName} (${routingDisplay.confidence}%)`);
      console.log(`📝 Reasoning: ${routingDisplay.reasoning}`);

      // Handle document rewriter agent entry
      if (routing.tool === 'document_rewriter_agent') {
        setDocumentRewriterMode(true);
        setAgentStatus('🔄 Entering LangGraph Document Rewriter Agent...');

        // Add entry message
        const entryMessage = {
          role: 'assistant',
          content: `🔄 **Entering LangGraph Document Rewriter Agent**

I'm now in Document Rewriter mode using LangGraph technology and ready to help you create documents from templates.

**What I can do:**
- Analyze your request and suggest appropriate document templates
- Generate dynamic field mappings based on your input
- Allow you to edit and refine mappings through natural conversation
- Execute document creation with your final specifications

**Technology:** Powered by LangGraph for intelligent document processing

**To get started:** Tell me what type of document you'd like to create, or describe your specific needs.

**To exit:** Say "exit document rewriter" or "stop rewriting" when you're done.

What document would you like to create?`,
          agentMode: 'document_rewriter',
          showSources: false
        };
        setMessages(prev => [...prev, entryMessage]);

        // Process the initial request
        const rewriterResult = await handleDocumentRewriterAgent(query, openai, history);

        if (rewriterResult.success) {
          setMessages(prev => {
            const updated = [...prev];
            updated.push(rewriterResult.message);
            return updated;
          });
        } else {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ **LangGraph Document Rewriter Agent Failed**

**Error:** ${rewriterResult.message.content}

**Action:** Exiting document rewriter mode due to error.

**Next steps:** 
- Try again with a different request
- Check your API key and network connection
- Contact support if the issue persists`,
            showSources: false
          }]);
          setDocumentRewriterMode(false);
          resetDocumentRewriterAgent(); // Reset agent state when exiting
        }

        setAgentStatus('');
        setIsLoading(false);
        setAttachedFiles([]);
        return;
      }

      // STEP 2: Search memory first to enhance other searches
      let memoryContext = '';
      let enhancedQuery = query;

      if (memoryEnabled) {
        try {
          const memoryResponse = await fetch('/api/zep/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query: query,
              limit: 5
            })
          });

          if (memoryResponse.ok) {
            const memoryData = await memoryResponse.json();
            if (memoryData.success) {
              if (memoryData.results && memoryData.results.length > 0) {
                enhancedQuery = memoryData.enhanced_query || query;
                memoryContext = memoryData.formatted_context || "";

                setRelevantMemories(memoryData.results.map(result => ({
                  user_message: "Zep memory",
                  assistant_response: typeof result === 'string' ? result : result.fact,
                  similarity: typeof result === 'string' ? 0.8 : (result.score || 0.8),
                  timestamp: new Date().toISOString(),
                  corrections: []
                })));

                console.log(`🧠 [Agent Mode] Memory search found ${memoryData.results.length} results`);
              } else {
                memoryContext = "🧠 MEMORY SEARCH: No stored information found for this query. Memory system is active and available for storing new information.";
                setRelevantMemories([]);
              }
            }
          }
        } catch (memoryError) {
          console.warn('[Agent Mode] Memory search failed:', memoryError);
        }
      }

      // STEP 3: Execute tools based on routing decision
      let contextBlocks = [];
      let webCitations = [];
      let ragCitations = [];
      let toolsUsed = [];

      if (memoryContext) {
        contextBlocks.push(memoryContext);
      }

      // Execute web search
      if (routing.tool === 'web_search' || routing.tool === 'both') {
        setAgentStatus('🌐 Searching the web...');
        const webResult = await performWebSearch(enhancedQuery, openai, history, selectedModel);
        if (webResult.success) {
          contextBlocks.push(`Web Search Results:\n${webResult.content}`);
          webCitations = webResult.citations || [];
          toolsUsed.push('web_search');
        }
      }

      // Execute knowledge base search
      if (routing.tool === 'knowledge_base' || routing.tool === 'both') {
        setAgentStatus('📚 Searching knowledge base...');

        try {
          const ragResult = await searchRAG(enhancedQuery, openai, history, selectedModel, () => buildRAGSystemPrompt(customPrompt));

          if (ragResult.success) {
            contextBlocks.push(`Knowledge Base Results:\n${ragResult.content}`);
            ragCitations = ragResult.citations || [];
            toolsUsed.push('knowledge_base');
          } else {
            toolsUsed.push('knowledge_base_no_results');
          }
        } catch (kbError) {
          console.error('Knowledge base error:', kbError);
          toolsUsed.push('knowledge_base_error');
        }
      }

      // Add file content to context
      if (extractedFileTexts.length > 0) {
        const fileContext = extractedFileTexts.map(file => {
          const chunkedContent = chunkLargeText(file.content);
          return `[UPLOADED FILE: ${file.name}]\n${chunkedContent}`;
        }).join('\n\n');
        contextBlocks.push(`Uploaded files content:\n${fileContext}`);
      }

      // STEP 4: Generate final response
      setAgentStatus('🤖 Generating response...');

      let systemPrompt = buildSystemPrompt(customPrompt);

      if (memoryContext) {
        systemPrompt += `\n\n🧠 PRIORITY MEMORY CONTEXT:\n${memoryContext}`;
      }

      const otherContextBlocks = contextBlocks.filter(block => block !== memoryContext);
      if (otherContextBlocks.length > 0) {
        systemPrompt += `\n\nAdditional Context:\n${otherContextBlocks.join('\n\n')}`;
      }

      const stream = await openai.chat.completions.create({
        model: selectedModel,
        messages: [
          { role: 'system', content: systemPrompt },
          ...history
        ],
        temperature: 1.0,
        top_p: 1.0,
        presence_penalty: 0,
        frequency_penalty: 0,
        stream: true
      });

      let assistantContent = '';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '',
        webCitations: webCitations || [],
        ragCitations: ragCitations || [],
        showSources: false,
        agentDecision: {
          decision: routingDisplay.displayName,
          reasoning: `${routingDisplay.reasoning} | Tools used: ${toolsUsed.length > 0 ? toolsUsed.join(', ') : 'none'}`,
          confidence: routingDisplay.confidence
        }
      }]);

      for await (const chunk of stream) {
        if (abortStreamRef.current) break;
        const delta = chunk.choices?.[0]?.delta?.content || '';
        if (delta) {
          assistantContent += delta;
          const currentContent = assistantContent;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: currentContent };
            return updated;
          });
        }
      }

      // STEP 5: Enhanced Memory saving
      if (memoryEnabled && query && assistantContent) {
        try {
          setAgentStatus('🧠 Saving to memory...');

          const memoryResponse = await fetch('/api/zep/add_conversation_enhanced', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_message: query,
              assistant_response: assistantContent,
              session_id: sessionId,
              use_gpt_layer: true
            })
          });

          if (memoryResponse.ok) {
            const result = await memoryResponse.json();
            if (result.success && result.saved) {
              if (result.gpt_layer_used) {
                console.log(`🧠 [Agent] Enhanced memory saved: ${result.enhanced_memory?.substring(0, 100)}...`);
                toolsUsed.push('memory_save_gpt');
              } else {
                console.log(`🧠 [Agent] Memory saved: ${result.reason}`);
                toolsUsed.push('memory_save');
              }
            }
          }
        } catch (memoryError) {
          console.warn('Enhanced memory save failed:', memoryError);
        }
      }

    } catch (error) {
      console.error('🚨 Agent Mode Error:', error);

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `🚨 Agent mode encountered an error: ${error.message}\n\nPlease try again or switch to manual mode for more control.`
      }]);
    }

    setAgentStatus('');
    setIsLoading(false);
    setAttachedFiles([]);
  };



  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      {sidebarVisible && (
        <Sidebar
          clearChat={clearChat}
          agentMode={agentMode}
          setAgentMode={setAgentMode}
          webSearchEnabled={webSearchEnabled}
          setWebSearchEnabled={setWebSearchEnabled}
          ragEnabled={ragEnabled}
          setRagEnabled={setRagEnabled}
          memoryEnabled={memoryEnabled}
          setMemoryEnabled={setMemoryEnabled}
          isDarkTheme={isDarkTheme}
          toggleTheme={toggleTheme}
          memoryStats={memoryStats}
          customPrompt={customPrompt}
          promptInputValue={promptInputValue}
          setPromptInputValue={setPromptInputValue}
          showPromptEditor={showPromptEditor}
          setShowPromptEditor={setShowPromptEditor}
          updateCustomPrompt={updateCustomPrompt}
          resetToDefaultPrompt={resetToDefaultPrompt}
        />
      )}

      <div className="main-content">
        <TopBar
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          documentRewriterMode={documentRewriterMode}
        />

        {/* Tab Content */}
        {activeTab === 'chat' && (
          <>
            <ChatMessages
              messages={messages}
              isLoading={isLoading}
              messagesEndRef={messagesEndRef}
              setMessages={setMessages}
            />

            {/* Memory Panel */}
            {showMemoryPanel && relevantMemories.length > 0 && (
              <div style={{
                padding: '12px 20px',
                backgroundColor: 'var(--bg-secondary)',
                borderTop: '1px solid var(--border-color)',
                fontSize: '13px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>🧠 Current Context from Memory</h4>
                {relevantMemories.map((memory, idx) => (
                  <div key={idx} style={{
                    marginBottom: '8px',
                    padding: '6px',
                    backgroundColor: 'var(--hover-color)',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div><strong>Previous Q:</strong> {memory.user_message.substring(0, 100)}...</div>
                    <div><strong>Previous A:</strong> {memory.assistant_response.substring(0, 100)}...</div>
                    {memory.corrections && memory.corrections.length > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        {memory.corrections.map((correction, corrIdx) => (
                          <div key={corrIdx} style={{ color: 'var(--error-color)', fontSize: '12px' }}>
                            <strong>CORRECTION:</strong> {correction.correction_text}
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Similarity: {memory.similarity.toFixed(2)} | {new Date(memory.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Agent Status Bar */}
            {agentMode && agentStatus && (
              <div style={{
                padding: '8px 20px',
                backgroundColor: 'rgba(74, 222, 128, 0.1)',
                borderTop: '1px solid var(--border-color)',
                fontSize: '12px',
                color: 'var(--text-primary)',
                fontWeight: '500'
              }}>
                {agentStatus}
              </div>
            )}

            <ChatInput
              input={input}
              setInput={setInput}
              isLoading={isLoading}
              textareaRef={textareaRef}
              handleKeyPress={handleKeyPress}
              handleFileChange={handleFileChange}
              attachedFiles={attachedFiles}
              removeAttachedFile={removeAttachedFile}
              sendMessage={sendMessage}
              sendMessageWithMemory={sendMessageWithMemory}
              sendMessageWithAgent={sendMessageWithAgent}
              agentMode={agentMode}
              memoryEnabled={memoryEnabled}
            />
          </>
        )}

        {/* Memory DAG Tab */}
        {activeTab === 'memory' && (
          <ZepMemory />
        )}
      </div>
    </div>
  );
};

export default App;
